/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { SelectValue } from "@duxton-web/ui/es/select"
import { useLocaleContext } from "common/utils/i18n"
import {
  CustomSelect,
  OptionValue as SelectOption,
} from "components/common/CustomSelect"
import { DEFAULT_PAGE_SIZE_BRAND_NAME } from "constants/menu"
import { Form } from "grab-ui"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useMemo, useState, useEffect } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/duck/menu"
import { AppState } from "store/store.type"
import { ItemAttributeValue } from "types/models/item_attribute"
import { attributeToItemAttributeValue } from "utils/attribute_helper"
import { fetchListBrandsSearchWithMigration } from "utils/rtk_migration_helper"
import { useLazyFetchListBrandsSearchQuery } from "queries/menuApi"

interface BrandSelectFieldProps {
  disabled?: boolean
  onChange?: (brand: ItemAttributeValue) => void
  value?: ItemAttributeValue
}

function BrandSelectField({
  disabled,
  onChange,
  value: currentItemBrand,
}: BrandSelectFieldProps) {
  const [page, setPage] = useState(1)
  const [searchValue, setSearchValue] = useState("")
  const [brandOptionState, setBrandOptionState] = useState<SelectOption[]>([])

  const textMap = useTranslationsMapTyped({
    labelBrand: "menu.items.headings.brand",
    placeholderField: "menu.item.placeholder.brandField",
    placeholderSearch: "menu.item.placeholder.brandSearch",
  })
  const dispatch = useDispatch()
  const { brands, merchantID, isLoading, hasMore } = useSelector(
    (s: AppState) => ({
      brands: s.menu.brands.data,
      hasMore: s.menu.brands.hasMore,
      isLoading: s.menu.brands.isLoading,
      forLocale: s.menu.brands.forLocale,
      merchantID: s.menu.outletV2?.ID,
    }),
    /* istanbul ignore next because this is just an optimisation to prevent useless re-renders */
    (prev, after) => {
      if (prev.isLoading && prev.isLoading === after.isLoading) {
        return true
      }
      return shallowEqual(prev, after)
    },
  )
  const locale = useLocaleContext("locale")

  const {
    flags: { fetchListBrandsSearch: isRtkFetchListBrandsSearchEnabled },
    isLoading: isRtkFlagLoading,
  } = useRtkQueryMigrationEndpointsFlag()

  const [fetchListBrandsSearchRtk] = useLazyFetchListBrandsSearchQuery()

  const fetchListBrandsSearch = (queryParams: {
    page: number
    locale: string
    name: string
  }) => {
    if (!merchantID) return

    // While migration flags are loading, do not fall back to old Redux: the enabled bit is
    // unknown until GrabX resolves. Legacy-then-RTK for the same user intent can double-call
    // APIs/middleware and splits RTK migration tracking. Wait for !isRtkFlagLoading; disable
    // the trigger UI while loading so clicks are not silently dropped.
    if (isRtkFlagLoading) return

    fetchListBrandsSearchWithMigration({
      dispatch,
      dispatchLegacyFetch: () =>
        dispatch(actionCreator.fetchListBrandsSearch(merchantID, queryParams)),
      fetchListBrandsSearchRtk,
      isRtkFetchListBrandsSearchEnabled,
      merchantID,
      queryParams,
    })
  }

  useEffect(() => {
    if (merchantID && !disabled) {
      fetchListBrandsSearch({
        page: 1,
        locale,
        name: "",
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    locale,
    merchantID,
    disabled,
    isRtkFlagLoading,
    isRtkFetchListBrandsSearchEnabled,
  ])

  useEffect(() => {
    const brandOptions: SelectOption[] = []
    if (brands?.length && !isLoading) {
      brandOptions.push(
        ...brands
          .filter(
            brand =>
              brand.cluster.toLowerCase() === "brand" &&
              brand.status.toLowerCase() === "enabled",
          )
          .map(brand => ({
            label: brand.name,
            value: brand.id,
            customLabelNode: (
              <div>
                <span>{brand.name}</span>
              </div>
            ),
          })),
      )
    }

    setBrandOptionState(brandOptions)
  }, [brands, isLoading])

  const handleSearchBrandName = (val: string) => {
    if (!merchantID || val === "") {
      return
    }

    if (val.length < 2) {
      setBrandOptionState([])
    } else {
      setPage(1)
      setSearchValue(val)

      fetchListBrandsSearch({
        page: 1,
        locale,
        name: val,
      })
    }
  }

  const handleBrandNamePaginationChange = (val: number) => {
    if (!merchantID) {
      return
    }

    setPage(val)

    fetchListBrandsSearch({
      page: val,
      locale,
      name: searchValue,
    })
  }

  const total =
    hasMore && brandOptionState
      ? page * brandOptionState.length + DEFAULT_PAGE_SIZE_BRAND_NAME
      : page * DEFAULT_PAGE_SIZE_BRAND_NAME

  return (
    <Form.Item label={textMap.labelBrand}>
      <CustomSelect
        placeholder={textMap.placeholderField}
        searchProps={useMemo(
          () => ({
            placeholder: textMap.placeholderSearch,
          }),
          [textMap.placeholderSearch],
        )}
        disabled={disabled || isRtkFlagLoading}
        data-testid="select-brand"
        options={brandOptionState}
        searchable
        optionLabelProp="title"
        loading={isLoading}
        value={
          currentItemBrand &&
          ({
            key: currentItemBrand.attributeID,
            label: currentItemBrand.value,
          } as SelectValue)
        }
        labelInValue
        onChange={val => {
          if (brands) {
            // @ts-ignore
            const selectedBrand = brands.find(b => b.id === val.key)
            if (selectedBrand) {
              onChange?.(attributeToItemAttributeValue(selectedBrand))
            }
          }
        }}
        onSearch={handleSearchBrandName}
        paginationProps={{
          current: page,
          showPagination: Boolean(brandOptionState && brandOptionState.length),
          onChange: handleBrandNamePaginationChange,
          pageSize: DEFAULT_PAGE_SIZE_BRAND_NAME,
          total,
          hideOnSinglePage: true,
        }}
      />
    </Form.Item>
  )
}

export default BrandSelectField
